import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  Copy,
  ExternalLink,
  Inbox,
  Loader2,
  Plus,
  Smartphone,
  Trash2,
} from 'lucide-react';
import {
  ajouterTransfertBoite,
  fetchBoitesMail,
  fetchNonLusBoitesMail,
  fetchParametresBoite,
  fetchTransfertsBoite,
  ouvrirBoiteMail,
  retirerTransfertBoite,
  revelerMotDePasseBoite,
} from '../services/api';

/**
 * Les boîtes de l'association que le membre peut ouvrir, sans mot de passe.
 *
 * Chaque fonction a la sienne — président, secrétaire, trésorier, chacune
 * partagée avec son vice — et la boîte contact est à tout le monde. Le clic
 * demande au serveur une session webmail et l'ouvre dans un onglet : aucun
 * mot de passe ne circule, ni ne se perd aux élections suivantes.
 *
 * Le bouton téléphone déplie la configuration du client mail : profil Apple
 * prêt à installer, réglages IMAP/SMTP à copier pour Android. Seul le mot de
 * passe de la boîte reste à saisir sur l'appareil — il ne sort jamais d'ici.
 *
 * Le composant s'efface quand l'instance n'a pas de messagerie configurée :
 * pas de boîte, pas de carte.
 */
export default function BoitesMail() {
  const { data } = useQuery({ queryKey: ['boites-mail'], queryFn: fetchBoitesMail });
  // Les non-lus arrivent après la liste : le serveur interroge l'IMAP de
  // chaque boîte, l'écran ne l'attend pas pour se peindre. Rafraîchis toutes
  // les deux minutes tant que l'écran reste ouvert.
  const nonLusQuery = useQuery({
    queryKey: ['boites-mail-non-lus'],
    queryFn: fetchNonLusBoitesMail,
    enabled: (data?.boites ?? []).length > 0,
    refetchInterval: 120000,
    staleTime: 60000,
  });
  const nonLus = nonLusQuery.data?.nonLus ?? {};
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [configOuverte, setConfigOuverte] = useState<string | null>(null);

  const ouvrir = useMutation({
    mutationFn: (code: string) => ouvrirBoiteMail(code),
    onSuccess: (d: any) => {
      if (d.url) {
        window.open(d.url, '_blank', 'noopener');
      }
    },
    onError: (e: Error) => setErreur(e.message),
    onSettled: () => setEnCours(null),
  });

  const boites = data?.boites ?? [];
  if (boites.length === 0) return null;

  return (
    <div className="card lg:p-6 space-y-3">
      <h2 className="font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100">
        <Inbox className="w-5 h-5 text-primary-600" /> Messagerie de l'association
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Les boîtes que votre fonction vous ouvre, sans mot de passe à retenir.
      </p>

      {erreur && (
        <p className="text-sm rounded-xl px-3 py-2 bg-red-50 text-red-700 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          {erreur}
        </p>
      )}

      <ul className="space-y-2">
        {boites.map((b) => (
          <li key={b.code} className="rounded-xl border border-gray-100 dark:border-gray-700">
            <div className="flex items-center gap-1.5 px-3 py-2.5">
              <button
                type="button"
                onClick={() => {
                  setErreur(null);
                  setEnCours(b.code);
                  ouvrir.mutate(b.code);
                }}
                disabled={ouvrir.isPending}
                className="flex-1 min-w-0 flex items-center gap-3 text-left disabled:opacity-60"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    {b.libelle}
                    {/* Le badge ne paraît que si le serveur a pu compter :
                        pas de zéro trompeur quand l'IMAP n'a pas répondu. */}
                    {typeof nonLus[b.code] === 'number' && (
                      <span
                        className={`text-[11px] font-bold rounded-full px-1.5 py-0.5 leading-none ${
                          (nonLus[b.code] as number) > 0
                            ? 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/40 dark:text-secondary-300'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }`}
                        title={`${nonLus[b.code]} message(s) non lu(s)`}
                      >
                        {nonLus[b.code]} non lu{(nonLus[b.code] as number) > 1 ? 's' : ''}
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{b.adresse}</p>
                </div>
                {enCours === b.code && ouvrir.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin text-primary-600 shrink-0" />
                ) : (
                  <ExternalLink className="w-4 h-4 text-gray-400 shrink-0" />
                )}
              </button>
              <button
                type="button"
                title="Configurer sur votre téléphone"
                onClick={() => setConfigOuverte(configOuverte === b.code ? null : b.code)}
                className={`p-2 rounded-lg transition shrink-0 ${
                  configOuverte === b.code
                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                    : 'text-gray-400 hover:text-primary-600'
                }`}
              >
                <Smartphone className="w-4 h-4" />
              </button>
            </div>
            {configOuverte === b.code && <ConfigTelephone code={b.code} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * La configuration mobile d'une boîte : profil Apple à installer, réglages
 * IMAP/SMTP à copier pour Android.
 */
function ConfigTelephone({ code }: { code: string }) {
  const { data, isPending } = useQuery({
    queryKey: ['boite-parametres', code],
    queryFn: () => fetchParametresBoite(code),
  });

  if (isPending) {
    return (
      <div className="border-t border-gray-100 dark:border-gray-700 p-3 flex justify-center">
        <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 p-3 space-y-3 text-sm">
      <div>
        <p className="font-medium text-gray-900 dark:text-gray-100">🌐 Webmail — {data.association}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          L'adresse à retenir pour relever la boîte depuis n'importe quel navigateur, avec son mot
          de passe :
        </p>
        <a
          href={data.webmail}
          target="_blank"
          rel="noopener"
          className="mt-1 inline-block font-mono text-xs text-primary-700 dark:text-primary-300 hover:underline"
        >
          {data.webmail}
        </a>
      </div>

      <div>
        <p className="font-medium text-gray-900 dark:text-gray-100">🍏 iPhone / iPad</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          Téléchargez le profil, puis Réglages → Profil téléchargé → Installer. Le compte arrive
          configuré, seul le mot de passe de la boîte vous sera demandé.
        </p>
        {/* Lien direct : Safari reconnaît le profil et propose l'installation,
            ce qu'un téléchargement par script ne déclencherait pas. */}
        <a
          href={`/api/bureau/webmail/${code}/apple.mobileconfig`}
          className="mt-1.5 inline-flex items-center gap-1.5 text-primary-700 dark:text-primary-300 font-medium hover:underline"
        >
          <Smartphone className="w-4 h-4" /> Télécharger le profil Apple
        </a>
      </div>

      <div>
        <p className="font-medium text-gray-900 dark:text-gray-100">🤖 Android et autres clients</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-1.5">
          Ajoutez un compte IMAP dans Gmail ou votre application mail, avec ces réglages :
        </p>
        <LigneParametre libelle="Adresse et identifiant" valeur={data.identifiant} />
        <LigneParametre
          libelle={`Serveur entrant (IMAP, port ${data.imap.port}, ${data.imap.securite})`}
          valeur={data.imap.serveur}
        />
        <LigneParametre
          libelle={`Serveur sortant (SMTP, port ${data.smtp.port}, ${data.smtp.securite})`}
          valeur={data.smtp.serveur}
        />
      </div>

      <MotDePasseBoite code={code} />

      {/*
        Le transfert juste après les réglages du client mail : les deux
        répondent à la même question — « comment est-ce que je lis ce courrier
        sans ouvrir un second webmail ? » — et la deuxième réponse est de loin
        la plus employée.
      */}
      <TransfertsBoite code={code} />
    </div>
  );
}

/**
 * Les adresses vers lesquelles cette boîte recopie son courrier.
 *
 * POURQUOI CET ÉCRAN EXISTE
 * ---------------------------------------------------------------------------
 * Un président relève ses messages sur son adresse personnelle, comme tout le
 * monde. La boîte de l'association dort — et le courrier important y dort avec
 * elle, jusqu'à ce qu'on découvre un mois plus tard une relance de la
 * fédération.
 *
 * Deux issues seulement : lui demander d'ouvrir un second webmail tous les
 * matins — ce qu'il ne fera pas — ou faire suivre.
 *
 * CE QUE L'ÉCRAN DIT, ET POURQUOI IL LE DIT
 * ---------------------------------------------------------------------------
 * Que le message RESTE dans la boîte de l'association. Sans cette phrase, le
 * réflexe est de croire qu'on vide la boîte commune dans sa boîte perso — et
 * l'on hésite, à juste titre, parce que le successeur ne retrouverait rien.
 */
function TransfertsBoite({ code }: { code: string }) {
  const [liste, setListe] = useState<string[] | null>(null);
  const [saisie, setSaisie] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const charger = useCallback(() => {
    fetchTransfertsBoite(code)
      .then((r) => setListe(r.transferts ?? []))
      .catch((e: Error) => setErreur(e.message));
  }, [code]);

  useEffect(charger, [charger]);

  const ajouter = async () => {
    const adresse = saisie.trim();
    if (adresse === '') return;

    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      const r = (await ajouterTransfertBoite(code, adresse)) as { message?: string };
      setMessage(r.message ?? null);
      setSaisie('');
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  const retirer = async (adresse: string) => {
    if (
      !window.confirm(
        `Arrêter la recopie vers ${adresse} ?\n\n` +
          'Les messages déjà transférés restent chez leur destinataire : ' +
          'un courrier reçu ne se reprend pas.',
      )
    ) {
      return;
    }

    setErreur(null);
    setMessage(null);
    setEnCours(true);
    try {
      const r = (await retirerTransfertBoite(code, adresse)) as { message?: string };
      setMessage(r.message ?? null);
      charger();
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
      <p className="font-medium text-gray-900 dark:text-gray-100">📨 Recopier vers une autre adresse</p>
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-2">
        Chaque message reçu ici part aussi vers les adresses ci-dessous.{' '}
        <strong>Il reste dans la boîte de l’association</strong> : le jour où la fonction change,
        le successeur retrouve tout l’historique.
      </p>

      {liste === null ? (
        <p className="text-xs text-gray-400">Chargement…</p>
      ) : (
        <ul className="space-y-1 mb-2">
          {liste.map((adresse) => (
            <li key={adresse} className="flex items-center gap-2 text-sm">
              <span className="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-200">
                {adresse}
              </span>
              <button
                type="button"
                disabled={enCours}
                onClick={() => void retirer(adresse)}
                aria-label={`Arrêter la recopie vers ${adresse}`}
                title="Arrêter la recopie"
                className="shrink-0 inline-flex items-center justify-center w-11 h-11 sm:w-8 sm:h-8 text-gray-400 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}

          {liste.length === 0 && (
            <li className="text-xs text-gray-500 dark:text-gray-400">
              Aucune recopie : le courrier ne part nulle part.
            </li>
          )}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          type="email"
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void ajouter();
            }
          }}
          placeholder="prenom.nom@exemple.fr"
          aria-label="Adresse vers laquelle recopier"
          className="flex-1 min-w-[14rem] rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
        />
        <button
          type="button"
          disabled={enCours || saisie.trim() === ''}
          onClick={() => void ajouter()}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 min-h-[44px] disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Ajouter un transfert
        </button>
      </div>

      {message !== null && (
        <p className="mt-2 text-sm text-green-700 dark:text-green-400">{message}</p>
      )}
      {erreur !== null && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-400">
          {erreur}
        </p>
      )}
    </div>
  );
}

/**
 * Le mot de passe de la boîte, révélé après re-saisie du mot de passe du
 * compte : l'écran resté ouvert ne le donne pas d'un simple clic.
 */
function MotDePasseBoite({ code }: { code: string }) {
  const [ouvert, setOuvert] = useState(false);
  const [saisie, setSaisie] = useState('');
  const [revele, setRevele] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [copie, setCopie] = useState(false);

  const reveler = async () => {
    setErreur(null);
    setEnCours(true);
    try {
      const reponse = await revelerMotDePasseBoite(code, saisie);
      setRevele(reponse.motDePasse);
      setSaisie('');
    } catch (e) {
      setErreur((e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div>
      <p className="font-medium text-gray-900 dark:text-gray-100">🔑 Mot de passe de la boîte</p>
      {revele !== null ? (
        <button
          type="button"
          onClick={() => {
            navigator.clipboard?.writeText(revele).then(() => {
              setCopie(true);
              setTimeout(() => setCopie(false), 1500);
            });
          }}
          className="mt-1.5 inline-flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-1.5 font-mono text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          title="Copier"
        >
          {revele}
          {copie ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5 text-gray-400" />}
        </button>
      ) : !ouvert ? (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Nécessaire une seule fois, pour le webmail ou la première configuration du téléphone.
          </p>
          <button
            type="button"
            onClick={() => setOuvert(true)}
            className="mt-1.5 text-primary-700 dark:text-primary-300 font-medium hover:underline"
          >
            Afficher le mot de passe…
          </button>
        </>
      ) : (
        <div className="mt-1.5 space-y-1.5">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Confirmez votre identité : saisissez le mot de passe de <strong>votre compte</strong>.
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              className="input flex-1 text-sm py-1.5"
              placeholder="Votre mot de passe de connexion"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && saisie && reveler()}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={reveler}
              disabled={!saisie || enCours}
              className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {enCours && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Afficher
            </button>
          </div>
          {erreur && <p className="text-xs text-red-600">{erreur}</p>}
        </div>
      )}
    </div>
  );
}

function LigneParametre({ libelle, valeur }: { libelle: string; valeur: string }) {
  const [copie, setCopie] = useState(false);

  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard?.writeText(valeur).then(() => {
          setCopie(true);
          setTimeout(() => setCopie(false), 1500);
        });
      }}
      className="w-full flex items-center gap-2 rounded-lg px-2.5 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
      title="Copier"
    >
      <span className="flex-1 min-w-0">
        <span className="block text-xs text-gray-500 dark:text-gray-400">{libelle}</span>
        <span className="block font-mono text-xs text-gray-900 dark:text-gray-100 truncate">{valeur}</span>
      </span>
      {copie ? (
        <Check className="w-3.5 h-3.5 text-green-600 shrink-0" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      )}
    </button>
  );
}
